import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { navItemForPath } from "./nav-items";

const APP_NAME = "Carselona Admin";

/**
 * What happens when someone arrives on a page inside the app.
 *
 * 1. The browser tab is named after the screen — "Call logs · Carselona
 *    Admin" — so an admin with five tabs open can tell them apart. Every tab
 *    used to read "Carselona Admin".
 * 2. Focus moves to the page content. A client-side navigation otherwise
 *    leaves a screen reader on the link that was clicked, with no sign the
 *    page changed (WCAG: focus on route change). Skipped on the first load,
 *    where the browser already starts at the top, and when only the query
 *    string changed — a filter is not a new page.
 *
 * Returns the ref for the `<main>` element.
 */
export function usePageArrival() {
  const { pathname } = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const firstRender = useRef(true);

  useEffect(() => {
    const item = navItemForPath(pathname);
    document.title = item ? `${item.label} · ${APP_NAME}` : APP_NAME;

    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    // No scroll: ScrollRestoration owns the position.
    mainRef.current?.focus({ preventScroll: true });
  }, [pathname]);

  return mainRef;
}
